import { useCallback, useEffect, useState, useMemo } from 'react';
import useAgentsChats from '../useAgentsChats';
import { TextFieldWithButtonV2 } from '../../common/ai-chat/input-atoms/TextFieldWithButtonV2';
import { useSelector } from 'react-redux';
import { selectActiveChatId } from '../agentsChatsReducer';
import { useAgentChatInstance } from '../AgentChatInstanceContext';
import AgentInputModeSelector from './AgentInputModeSelector';
import { AgentInputMode } from './agentInputModeTypes';

const AgentInput = ({
  onConfirm,
  isLoading,
  disabled,
  selectedText,
  onClearSelectedText,
  placeholderText,
  renderStopButton,
  onStopClick,
  menuDirection = 'down',
}: {
  onConfirm: (
    message: string,
    options?: {
      advancedMode?: AgentInputMode;
      additionalOrgIds?: string[];
    },
  ) => Promise<void>;
  isLoading: boolean;
  disabled: boolean;
  selectedText?: string | null;
  onClearSelectedText?: () => void;
  placeholderText?: string;
  renderStopButton?: boolean;
  onStopClick?: () => void;
  menuDirection?: 'up' | 'down';
}) => {
  const [value, setValue] = useState('');
  const { chatInstanceId } = useAgentChatInstance();
  const activeChatId = useSelector(selectActiveChatId(chatInstanceId));
  const {
    inputPlaceholder: placeholderTextByType,
    advancedMode,
    handleModeChange,
    enableAdvancedMode,
    selectedAdditionalOrgIds,
  } = useAgentsChats();

  //reset the input when switching chats
  useEffect(() => {
    setValue('');
  }, [activeChatId]);

  const leftAdornment = useMemo(() => {
    if (!enableAdvancedMode) return undefined;
    return (
      <AgentInputModeSelector
        selectedMode={advancedMode}
        onModeChange={handleModeChange}
        menuDirection={menuDirection}
      />
    );
  }, [enableAdvancedMode, advancedMode, handleModeChange, menuDirection]);

  const handleConfirm = useCallback(
    async (message: string) => {
      // Pass additionalOrgIds only when in multi-org mode and orgs are selected
      const additionalOrgIds =
        advancedMode === AgentInputMode.MultiOrgMode && selectedAdditionalOrgIds.length > 0
          ? selectedAdditionalOrgIds
          : undefined;
      await onConfirm(message, { advancedMode, additionalOrgIds });
    },
    [onConfirm, advancedMode, selectedAdditionalOrgIds],
  );

  return (
    <TextFieldWithButtonV2
      value={value}
      setValue={setValue}
      onConfirm={handleConfirm}
      inputPlaceholder={placeholderText ?? placeholderTextByType}
      isLoading={isLoading}
      readonly={disabled}
      selectedText={selectedText}
      onClearSelectedText={onClearSelectedText}
      maxInputHeight={400}
      renderStopButton={renderStopButton}
      onStopClick={onStopClick}
      leftAdornment={leftAdornment}
      leftAdornmentOnSeparateLine={advancedMode !== AgentInputMode.None}
    />
  );
};

export default AgentInput;
